import { getRequestOptions } from '../helpers/Fetchwrapper'

class ShipmentService {
    static API_BASE_URL = process.env.REACT_APP_API_URL

    /**
     * Get shipments list with filtering and pagination
     */
    static async getShipmentsList(params = {}) {
        try {
            const queryParams = new URLSearchParams({
                offset: params.offset || 0,
                limit: params.limit || 10,
                orderBy: params.orderBy || 'salesShipmentId',
                orderType: params.orderType || 'desc',
                globalSearch: params.globalSearch || '',
                ...params
            })

            const response = await fetch(`${this.API_BASE_URL}shipments/list?${queryParams}`, getRequestOptions())
            const result = await response.json()
            return result
        } catch (error) {
            console.error('Error fetching shipments list:', error)
            throw error
        }
    }

    /**
     * Get shipment details by ID
     */
    static async getShipmentDetails(shipmentId) {
        try {
            const response = await fetch(`${this.API_BASE_URL}shipment/details/${shipmentId}`, getRequestOptions())
            const result = await response.json()
            return result
        } catch (error) {
            console.error('Error fetching shipment details:', error)
            throw error
        }
    }

    /**
     * Get order details for creating shipment
     */
    static async getOrderDetails(orderId) {
        try {
            const response = await fetch(`${this.API_BASE_URL}shipments/order/${orderId}`, getRequestOptions())
            const result = await response.json()
            return result
        } catch (error) {
            console.error('Error fetching order details for shipment:', error)
            throw error
        }
    }

    /**
     * Create new shipment
     */
    static async createShipment(shipmentData) {
        try {
            const response = await fetch(`${this.API_BASE_URL}shipments/create`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...getRequestOptions().headers
                },
                body: JSON.stringify(shipmentData)
            })
            const result = await response.json()
            return result
        } catch (error) {
            console.error('Error creating shipment:', error)
            throw error
        }
    }

    /**
     * Add carrier/tracking information
     */
    static async addCarrier(carrierData) {
        try {
            const response = await fetch(`${this.API_BASE_URL}shipments/carrier/add`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...getRequestOptions().headers
                },
                body: JSON.stringify(carrierData)
            })
            const result = await response.json()
            return result
        } catch (error) {
            console.error('Error adding carrier:', error)
            throw error
        }
    }

    /**
     * Delete carrier/tracking information
     */
    static async deleteCarrier(trackId) {
        try {
            const response = await fetch(`${this.API_BASE_URL}shipments/carrier/${trackId}`, {
                method: 'DELETE',
                headers: {
                    ...getRequestOptions().headers
                }
            })
            const result = await response.json()
            return result
        } catch (error) {
            console.error('Error deleting carrier:', error)
            throw error
        }
    }

    /**
     * Mark shipment as delivered
     */
    static async markAsDelivered(shipmentId, deliveryData) {
        try {
            const response = await fetch(`${this.API_BASE_URL}shipments/delivered`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...getRequestOptions().headers
                },
                body: JSON.stringify({
                    salesShipmentId: shipmentId,
                    ...deliveryData
                })
            })
            const result = await response.json()
            return result
        } catch (error) {
            console.error('Error marking shipment as delivered:', error)
            throw error
        }
    }

    /**
     * Get shipment print data
     */
    static async getShipmentPrint(shipmentId) {
        try {
            const response = await fetch(`${this.API_BASE_URL}shipments/print/${shipmentId}`, getRequestOptions())
            const result = await response.json()
            return result
        } catch (error) {
            console.error('Error fetching shipment print data:', error)
            throw error
        }
    }

    /**
     * Format shipment status
     */
    static formatShipmentStatus(status) {
        const statusMap = {
            0: { label: 'Pending', color: 'warning', icon: 'clock' },
            1: { label: 'Delivered', color: 'success', icon: 'check-circle' },
            2: { label: 'In Transit', color: 'info', icon: 'truck' },
            3: { label: 'Out for Delivery', color: 'primary', icon: 'shipping-fast' }
        }
        return statusMap[status] || { label: 'Unknown', color: 'secondary', icon: 'question-circle' }
    }

    /**
     * Format currency
     */
    static formatCurrency(amount) {
        return new Intl.NumberFormat('en-BD', {
            style: 'currency',
            currency: 'BDT',
            minimumFractionDigits: 2
        }).format(amount || 0)
    }

    /**
     * Format date
     */
    static formatDate(dateString) {
        if (!dateString) return 'N/A'
        const date = new Date(dateString)
        return date.toLocaleDateString('en-GB', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        })
    }

    /**
     * Generate shipment number
     */
    static generateShipmentNumber() {
        const timestamp = Date.now()
        const random = Math.floor(Math.random() * 1000)
        return `SH${timestamp}${random}`
    }

    /**
     * Validate shipment data
     */
    static validateShipmentData(data) {
        const errors = []

        if (!data.orderId) {
            errors.push('Order ID is required')
        }

        if (!data.items || data.items.length === 0) {
            errors.push('At least one item must be selected')
        }

        if (data.items) {
            data.items.forEach((item, index) => {
                if (!item.qtyShip || item.qtyShip <= 0) {
                    errors.push(`Item ${index + 1}: Quantity must be greater than 0`)
                }
                if (item.qtyShip > item.maxShip) {
                    errors.push(`Item ${index + 1}: Quantity cannot exceed available quantity (${item.maxShip})`)
                }
            })
        }

        return errors
    }

    /**
     * Calculate shipment totals
     */
    static calculateShipmentTotals(items) {
        return items.reduce((totals, item) => {
            totals.totalQty += parseInt(item.qtyShip) || 0
            totals.totalItems += 1
            return totals
        }, { totalQty: 0, totalItems: 0 })
    }
}

export default ShipmentService 